import { assert, present } from "@bodil/core/assert";
import { attribute, Component, type Deps, type Disposables } from "@bodil/dom/component";
import { findActiveElement } from "@bodil/dom/dom";
import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";

import { KeyboardCommand, keymapLookup } from "./keymap";
import { BtkStack } from "./stack";
import { BtkTab } from "./tab";
import { BtkTabPanel } from "./tab-panel";

import styles from "./tab-stack.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-tab-stack": BtkTabStack;
    }
}

@customElement("btk-tab-stack")
export class BtkTabStack extends Component {
    static deps: Deps = [BtkStack];

    @attribute accessor direction: "horizontal" | "vertical" = "horizontal";

    get tabs(): Array<BtkTab> {
        return this.querySlot({ slot: "nav", flatten: true, instanceOf: BtkTab });
    }

    get panels(): Array<BtkTabPanel> {
        return this.querySlot({ flatten: true, instanceOf: BtkTabPanel });
    }

    get activeTab(): BtkTab | undefined {
        return this.tabs.find((tab) => tab.active);
    }

    private readonly stack = createRef<BtkStack>();

    protected override connected(): Disposables {
        super.connected();
        this.useWhileConnected(this.on("keydown", this.handleKeydown.bind(this)));
    }

    setActiveTab(activeTab: BtkTab): Promise<void> {
        let activeIndex: number | null = null;
        this.tabs.forEach((tab, index) => {
            tab.active = tab === activeTab;
            if (tab.active) {
                activeIndex = index;
            }
        });
        if (activeIndex === null) {
            throw new Error("BtkTabStack: attempted to activate an unrecognised tab");
        }
        return present(this.stack.value).setPage(activeIndex);
    }

    private handleTabChange(e: Event) {
        if (!(e.target instanceof BtkTab)) {
            return;
        }
        void this.setActiveTab(e.target);
    }

    private handleKeydown(e: KeyboardEvent) {
        if (this.activeTab === undefined || findActiveElement(document) !== this.activeTab) {
            return;
        }
        let index = this.tabs.indexOf(this.activeTab);
        assert(index >= 0);
        const cmd = keymapLookup(e);
        if (cmd === (this.direction === "vertical" ? KeyboardCommand.Up : KeyboardCommand.Left)) {
            index -= 1;
            if (index < 0) {
                index = this.tabs.length - 1;
            }
        } else if (
            cmd === (this.direction === "vertical" ? KeyboardCommand.Down : KeyboardCommand.Right)
        ) {
            index += 1;
            if (index >= this.tabs.length) {
                index = 0;
            }
        } else if (cmd === KeyboardCommand.Top) {
            index = 0;
        } else if (cmd === KeyboardCommand.Bottom) {
            index = this.tabs.length - 1;
        } else {
            return;
        }
        e.preventDefault();
        void this.setActiveTab(this.tabs[index]);
        this.activeTab.focus();
    }

    protected override render(): unknown {
        return html`
            <nav part="nav" role="tablist" @change=${this.handleTabChange}>
                <slot name="nav"></slot>
            </nav>
            <btk-stack ${ref(this.stack)} part="stack" direction=${this.direction}>
                <slot></slot>
            </btk-stack>
        `;
    }

    private syncTabs() {
        assert(
            this.panels.length === this.tabs.length,
            `<btk-tab-stack>: tab count in "nav" slot (${this.tabs.length}) must match page count (${this.panels.length})`,
        );
        const pages = this.panels;
        this.tabs.forEach((tab, index) => {
            const page = pages[index];
            tab.setAttribute("aria-controls", page.getAttribute("id") ?? "");
            page.setAttribute("aria-labelledby", tab.getAttribute("id") ?? "");
        });
    }

    protected override firstUpdated(): void {
        super.firstUpdated();
        this.syncTabs();
        if (this.activeTab === undefined) {
            void this.setActiveTab(this.tabs[0]);
        }
    }

    static styles = styles;
}
